/**
 * A file's version history, and restoring one.
 *
 * Restoring does **not** rewind: it writes the old content as a *new* version on
 * top, so the history only ever grows and the thing you restored from is still
 * there. The dialog says so, because "restore" reads like an undo everywhere
 * else and the difference matters the moment somebody restores the wrong one.
 */
import { useTranslation } from 'react-i18next';
import DownloadIcon from '@mui/icons-material/Download';
import RestoreIcon from '@mui/icons-material/Restore';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { ResourceDto } from '@forsys/shared';
import { AsyncContent } from '../../components/AsyncContent';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { Timestamp } from '../../components/Timestamp';
import { useAsync } from '../../hooks/useAsync';
import { useSubmit } from '../../hooks/useSubmit';
import { contentUrl, listVersions, restoreVersion } from '../../api/resources';
import { formatBytes } from '../../utils/format';

export interface VersionsDialogProps {
  resource: ResourceDto;
  open: boolean;
  onClose: () => void;
  /** Called after a restore — the resource's `currentVersionId` has moved. */
  onChanged?: () => void;
}

export function VersionsDialog({ resource, open, onClose, onChanged }: VersionsDialogProps) {
  const { t } = useTranslation('files');
  const versions = useAsync(() => listVersions(resource.id), [resource.id], { enabled: open });
  const mayWrite = resource.capabilities.write;

  const restore = useSubmit(async (versionId: string) => {
    await restoreVersion(resource.id, versionId);
    versions.reload();
    onChanged?.();
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t('versions.title')}
        <Typography variant="body2" color="text.secondary" noWrap>
          {resource.name}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {restore.error && <ErrorState error={restore.error} severity="warning" />}

        <AsyncContent state={versions} placeholder="spinner">
          {({ versions: rows }) =>
            rows.length === 0 ? (
              <EmptyState
                dense
                title={t('versions.emptyTitle')}
                description={t('versions.emptyDescription')}
              />
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('versions.column.version')}</TableCell>
                    <TableCell>{t('versions.column.uploaded')}</TableCell>
                    <TableCell>{t('versions.column.size')}</TableCell>
                    <TableCell>{t('versions.column.type')}</TableCell>
                    <TableCell align="right">{t('versions.column.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((version) => {
                    const isCurrent = version.id === resource.currentVersionId;

                    return (
                      <TableRow key={version.id} hover>
                        <TableCell>
                          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {t('versions.number', { number: version.versionNumber })}
                            </Typography>
                            {isCurrent && (
                              <Chip size="small" color="success" label={t('versions.current')} />
                            )}
                            {version.restoredFromId && (
                              <Tooltip title={t('versions.restoredTooltip')}>
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={t('versions.restored')}
                                />
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Timestamp value={version.createdAt} />
                        </TableCell>
                        <TableCell>{formatBytes(version.sizeBytes)}</TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {version.contentType}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} sx={{ justifyContent: 'flex-end' }}>
                            <Tooltip title={t('versions.download')}>
                              <IconButton
                                size="small"
                                component="a"
                                href={contentUrl(resource.id, version.id)}
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {mayWrite && !isCurrent && (
                              <Tooltip title={t('versions.makeCurrent')}>
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={restore.pending}
                                    onClick={() => void restore.submit(version.id)}
                                  >
                                    <RestoreIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )
          }
        </AsyncContent>

        <Box sx={{ pt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            {t('versions.footnote')}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>{t('versions.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
